
import React, { useEffect, useRef, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, ssoEnabled } from '../../auth/authConfig';
import { isExplicitlySignedOut, LOGOUT_STATE_EVENT } from '../../auth/logoutState';
import styles from './ProtectedRoute.module.css';
import DisclaimerPopup, { hasAcceptedDisclaimer } from './DisclaimerPopup';

/**
 * ProtectedRoute — wraps any page/component that requires authentication.
 *
 * When SSO is disabled (ssoEnabled === false) this is a pass-through: children
 * render directly and NO MSAL hooks run (there is no <MsalProvider> mounted).
 * The MSAL-hook logic lives in the inner <SsoGate>, which only mounts when SSO
 * is on. React hooks cannot be called conditionally, so the early return here
 * must happen before any hook.
 *
 * @param {{ children: React.ReactNode }} props
 */
export default function ProtectedRoute({ children }) {
  if (!ssoEnabled) return children; // no hooks called before this return
  return <SsoGate>{children}</SsoGate>;
}

/**
 * SsoGate — the authenticated gate. Only mounted when SSO is enabled.
 *
 * Behaviour:
 * - While MSAL is initialising or handling a redirect → show a loading spinner.
 * - If the user is not authenticated and no interaction is in progress →
 *   automatically trigger loginRedirect (no gate page shown).
 * - If the user is authenticated → render children normally.
 * - If loginRedirect fails → logs to console only (no UI shown); does not retry.
 *
 * @param {{ children: React.ReactNode }} props
 */
function SsoGate({ children }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const redirectTriggered = useRef(false);
  const [explicitlySignedOut, setExplicitlySignedOut] = useState(isExplicitlySignedOut);
  const [signInError, setSignInError] = useState('');

  // Resolved MSAL account — used to key the disclaimer acceptance per user.
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;

  // Disclaimer state: true means user has already accepted (localStorage) or just accepted now.
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() =>
    hasAcceptedDisclaimer(account)
  );

  const isBusy =
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.AcquireToken;

  useEffect(() => {
    const syncLogoutState = () => setExplicitlySignedOut(isExplicitlySignedOut());
    window.addEventListener(LOGOUT_STATE_EVENT, syncLogoutState);
    window.addEventListener('storage', syncLogoutState);
    window.addEventListener('pageshow', syncLogoutState);
    window.addEventListener('focus', syncLogoutState);
    return () => {
      window.removeEventListener(LOGOUT_STATE_EVENT, syncLogoutState);
      window.removeEventListener('storage', syncLogoutState);
      window.removeEventListener('pageshow', syncLogoutState);
      window.removeEventListener('focus', syncLogoutState);
    };
  }, []);

  useEffect(() => {
    if (!explicitlySignedOut && !isAuthenticated && !isBusy && !redirectTriggered.current) {
      redirectTriggered.current = true;
      instance.loginRedirect(loginRequest).catch((err) => {
        // Do NOT reset redirectTriggered — prevents infinite retry loop.
        console.error('[Auth] loginRedirect error:', err);
      });
    }
  }, [explicitlySignedOut, isAuthenticated, isBusy, instance]);

  /** Starts sign-in only after the user explicitly requests it. */
  const handleSignIn = () => {
    setSignInError('');
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error('[Auth] loginRedirect error:', err);
      setSignInError('Unable to start sign in. Please try again.');
    });
  };

  if (explicitlySignedOut) {
    return (
      <main className={styles.gate}>
        <h1 className={styles.title}>You’re signed out</h1>
        <p className={styles.subtitle}>Sign in to access GCC Studio.</p>
        {signInError && <p className={styles.subtitle} role="alert">{signInError}</p>}
        <button
          type="button"
          className={styles.signInBtn}
          onClick={handleSignIn}
          disabled={inProgress !== InteractionStatus.None}
        >
          Sign in
        </button>
      </main>
    );
  }

  // Show spinner while MSAL is busy or the redirect is about to fire
  if (!isAuthenticated) {
    return (
      <main className={styles.gate}>
        <div
          className={styles.spinner}
          role="status"
          aria-label="Signing you in, please wait…"
        />
      </main>
    );
  }

  // Show disclaimer once per account immediately after authentication succeeds.
  if (!disclaimerAccepted) {
    return (
      <>
        {children}
        <DisclaimerPopup account={account} onAccept={() => setDisclaimerAccepted(true)} />
      </>
    );
  }

  return children;
}
